import React from 'react';

import './index.css';

import { Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, InputAdornment } from '@mui/material';
import { List, ListItem, ListItemText } from '@mui/material';
import { Box, Button, Divider, IconButton, TextField } from '@mui/material';
import { Select, InputLabel, Tooltip, FormControl, MenuItem, Checkbox } from '@mui/material';
import { Paper, InputBase, Popover, Grid } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import MessageIcon from '@mui/icons-material/Message';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ReactHtmlParser from 'react-html-parser';

import EmojiTextarea from '../../Input/EmojiTextarea';

import useNotification from "../../Notification";
import ConfirmationDialogCaptcha from '../ConfimationDialogCaptcha';

import { emojiReplaceJSX } from '../../../utils/emojiDict';
import { PredefinedDropdown } from '../../Settings/PredefinedMessages/PredefinedMessagesModal';
import { getSession } from "../../../auth"
import UserPermissions from '../../User/Permissions';

class PredefinedMessageDialog extends React.Component {

    constructor(props) {
        super(props);

        const allowShowAll = !getSession()?.profile?.enterprise?.hide_predefined_messages_show_all;

        this.state = {
            text: "",
            category: "",
            newCategory: "",
            predefinedMessages: {},
            showPopover: false,
            anchorEl: null,
            showAddCategory: false,
            openViewMessage: false,
            openEditMessage: false,
            editMessage: {},
            textEdit: "",
            queue: [0],
            showAll: localStorage.getItem('predefinedMessagesShowAll') === 'true' || false,
            search: "",
            searchAllCategories: false,
            showDeleteCategoryDialog: false,
            newEmptyCategories: new Set(),
        }
    }

    selectedItemSx = {
        '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)'
        },
        '&.Mui-selected:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.18)'
        }
    }

    normalizeQueueSelection = (queueSelection) => {
        const values = Array.isArray(queueSelection) ? queueSelection : [queueSelection];
        const normalized = [...new Set(
            values
                .map((value) => Number(value))
                .filter((value) => !Number.isNaN(value))
        )];

        if (normalized.length === 0) {
            return [0];
        }

        return normalized;
    }

    getMessageQueueIds = (message) => {
        const sourceQueueIds = Array.isArray(message?.queue_ids)
            ? message.queue_ids
            : Array.isArray(message?.queues)
                ? message.queues
                : [message?.queue_id];

        return [...new Set(
            sourceQueueIds
                .map((value) => {
                    if (typeof value === "object" && value !== null) {
                        return Number(value.id ?? value.queue_id ?? value.queue);
                    }
                    return Number(value);
                })
                .filter((value) => !Number.isNaN(value) && value > 0)
        )];
    }

    messageMatchesQueueSelection = (message, selectedQueueIds) => {
        if (!selectedQueueIds || selectedQueueIds.length === 0 || selectedQueueIds.includes(0)) {
            return true;
        }

        const messageQueueIds = this.getMessageQueueIds(message);

        if (messageQueueIds.length === 0) {
            return true;
        }

        return selectedQueueIds.some((queueId) => messageQueueIds.includes(queueId));
    }

    getAccessibleQueueIds = () => {
        const user = getSession();
        const permissions = UserPermissions();

        if (permissions.isAdmin) {
            return (user.profile?.enterprise?.queues || []).map((queue) => Number(queue.id));
        }

        const queueIds = new Set();
        (user.profile?.queues || []).forEach((queue) => queueIds.add(Number(queue.id)));

        if (permissions.isSupervisor) {
            (user.profile?.supervised_queues || []).forEach((queueId) => queueIds.add(Number(queueId)));
        }

        return Array.from(queueIds).filter((queueId) => !Number.isNaN(queueId));
    }

    getAvailableQueues = () => {
        const permissions = UserPermissions();
        const accessibleQueueIds = this.getAccessibleQueueIds();

        return (getSession().profile?.enterprise?.queues || [])
            .filter((queue) => !queue.name.split("_")[1].startsWith("0000"))
            .filter((queue) => permissions.isAdmin || accessibleQueueIds.includes(Number(queue.id)));
    }

    messageMatchesAccessibleQueues = (message) => {
        if (UserPermissions().isAdmin) {
            return true;
        }

        const messageQueueIds = this.getMessageQueueIds(message);

        if (messageQueueIds.length === 0) {
            return true;
        }

        const accessibleQueueIds = this.getAccessibleQueueIds();
        return messageQueueIds.some((queueId) => accessibleQueueIds.includes(queueId));
    }

    getActiveSearch = () => {
        const search = this.state.search.trim();
        return search.length >= 3 ? search.toLowerCase() : "";
    }

    messageMatchesSearch = (message) => {
        const search = this.getActiveSearch();

        if (!search) {
            return true;
        }

        return (message.message || "").toLowerCase().includes(search);
    }

    handleQueueChange = (event) => {
        const normalizedValues = this.normalizeQueueSelection(event.target.value);
        if (normalizedValues.includes(0) && normalizedValues.length > 1) {
            if ((Array.isArray(this.state.queue) ? this.state.queue : []).includes(0)) {
                this.setState({ queue: normalizedValues.filter((value) => value !== 0) });
            } else {
                this.setState({ queue: [0] });
            }
            return;
        }
        this.setState({ queue: normalizedValues });
    }

    usePredefinedMessage = (message) => {
        this.props.usePredefinedMessage(message);
        this.props.handleClose();
    }

    onEmojiClick = (event) => {
        let text = this.state.text;
        text += event.target.innerText;
        this.setState({
            text: text,
            showPopover: false,
            anchorEl: null
        });
    }

    onKeyDownPredefinedMessage = (event) => {
        // key shift off and enter on
        if (!event.shiftKey && (event.keyCode === 13 || event.key === 'Enter')) {
            this.addPredefinedMessage();
            event.preventDefault();
        }
    }

    onKeyDownEditMessage = (event) => {
        // key shift off and enter on
        if (!event.shiftKey && (event.keyCode === 13 || event.key === 'Enter')) {
            this.saveEditMessage();
            event.preventDefault();
        }
    }

    addPredefinedMessage = async () => {
        const text = this.state.text;

        if (text) {
            const category = this.state.category;
            const predefinedMessages = this.state.predefinedMessages;
            const messageData = await this.props.addPredefinedMessage(text, category, this.normalizeQueueSelection(this.state.queue));

            if (messageData) {
                if (! predefinedMessages[category]) {
                    predefinedMessages[category] = {};
                }
                predefinedMessages[category].push(messageData);

                const newEmptyCategories = new Set(this.state.newEmptyCategories);
                newEmptyCategories.delete(category);

                this.setState({
                    text: '',
                    predefinedMessages: predefinedMessages,
                    newEmptyCategories: newEmptyCategories,
                });
            }
        }
    }

    saveEditMessage = async () => {
        const text = this.state.textEdit;
        let editMessage = this.state.editMessage;
        editMessage.message = text;
        await this.props.updatePredefinedMessage(editMessage);

        this.setState({
            textEdit: '',
            editMessage: {},
            openEditMessage: false
        });
    }

    removePredefinedMessage = (message) => {
        this.props.removePredefinedMessage(message);

        const category = this.state.category;
        const predefinedMessages = this.state.predefinedMessages;
        for (var i = 0; i < predefinedMessages[category].length; i++) {
            if (predefinedMessages[category][i].id == message.id) {
                predefinedMessages[category].splice(i, 1);
            }
        }
        this.setState({
            predefinedMessages: predefinedMessages
        });
    }

    onChangeCategory = (event) => {
        const category = event.target.value;

        this.setState({ category: category });
    }

    onChangeNewCategory = (event) => {
        const newCategory = event.target.value;

        this.setState({ newCategory: newCategory });
    }

    addCategory = () => {
        let newCategory = this.state.newCategory;

        if (newCategory?.includes(' - Geral')){
            useNotification(
                'Erro!',
                'Categorias gerais não podem ser criadas aqui.',
                'danger'
            );
            return
        }

        if (newCategory) {
            let predefinedMessages = this.state.predefinedMessages;

            if (!predefinedMessages[newCategory]) {
                predefinedMessages[newCategory] = [];

                const newEmptyCategories = new Set(this.state.newEmptyCategories);
                newEmptyCategories.add(newCategory);

                this.setState({
                    predefinedMessages: predefinedMessages,
                    showAddCategory: false,
                    newEmptyCategories: newEmptyCategories,
                    category: newCategory,
                });

                useNotification(
                    'Sucesso!',
                    'Categoria adicionada com sucesso.',
                    'success'
                );
            } else {
                useNotification(
                    'Erro!',
                    'Categoria já existe.',
                    'danger'
                );
            }
        }
    }

    deleteCategory = async () => {
        const category = this.state.category;
        
        if (category?.includes(' - Geral')) {
            useNotification(
                'Erro!',
                'Categorias gerais não podem ser excluídas aqui.',
                'danger'
            );
            this.setState({ showDeleteCategoryDialog: false });
            return;
        }

        if (category && this.props.deleteCategory) {
            await this.props.deleteCategory(category);
            
            const predefinedMessages = { ...this.state.predefinedMessages };
            delete predefinedMessages[category];
            
            const newCategory = Object.keys(predefinedMessages)[0] || "";
            
            this.setState({ 
                predefinedMessages: predefinedMessages,
                category: newCategory,
                showDeleteCategoryDialog: false 
            });
        }
    }

    canDeleteCategory = () => {
        const category = this.state.category;

        return category && !category?.includes(' - Geral') && this.props.deleteCategory;
    }

    getFilteredMessages = (key, queue) => {
        let predefinedMessages = Array.isArray(this.state.predefinedMessages[key])
            ? this.state.predefinedMessages[key]
            : []
        const selectedQueueIds = this.normalizeQueueSelection(queue);

        predefinedMessages = predefinedMessages
            ? predefinedMessages.filter((message) => this.messageMatchesAccessibleQueues(message))
            : [];

        if (!selectedQueueIds.includes(0))
            predefinedMessages = predefinedMessages.filter((message) => this.messageMatchesQueueSelection(message, selectedQueueIds));

        return predefinedMessages.filter((message) => this.messageMatchesSearch(message));
    }

    renderPredefinedMessageItem = (message, key) => {
        const isHtmlMessage = message.is_html || (message.message && message.message.trim().startsWith('<html'));

        return (
            <ListItem className="item" key={key}>
                <>
                    {isHtmlMessage ? (
                        <Box display="flex" alignItems="center" gap={1} flex={1} sx={{ cursor: 'pointer' }}
                            onClick={() => this.usePredefinedMessage(message.message)}
                        >
                            <CodeIcon color="primary" fontSize="small" />
                            <ListItemText primary={message.title || 'Mensagem HTML'} />
                        </Box>
                    ) : (
                        <ListItemText sx={{overflow:'clip'}} primary={emojiReplaceJSX(message.message)}
                            onClick={() => this.usePredefinedMessage(message.message)}
                        />
                    )}
                    <Tooltip title="Visualizar" placement="bottom" disableInteractive>
                        <IconButton
                            onClick={() => {
                                this.setState({
                                    textEdit: message.message,
                                    editMessage: { ...message, is_html: isHtmlMessage },
                                    openViewMessage: true
                                });
                            }}
                            className="button-update"
                            size="large"
                        >
                            {isHtmlMessage ? <VisibilityIcon color="primary" className="icon-update" /> : <MessageIcon className="icon-update" />}
                        </IconButton>
                    </Tooltip>
                    {(message.user_id || message.user) &&
                        <>
                            <Tooltip title="Editar" placement="bottom" disableInteractive>
                                <IconButton
                                    onClick={() => {
                                        this.setState({
                                            textEdit: message.message,
                                            editMessage: message,
                                            openEditMessage: true
                                        });
                                    }}
                                    className="button-update"
                                    size="large"
                                >
                                    <EditIcon color="primary"
                                        className="icon-update"
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir" placement="bottom" disableInteractive>
                                <IconButton
                                    onClick={() => this.removePredefinedMessage(message)}
                                    className="button-delete"
                                    size="large">
                                    <DeleteIcon color="primary"
                                        className="icon-delete"
                                    />
                                </IconButton>
                            </Tooltip>
                        </>
                    }
                </>
            </ListItem>
        );
    }

    predefinedMessageSearchList = (queue) => {
        const predefinedMessageList = []

        Object.keys(this.state.predefinedMessages).map((category) => {
            const predefinedMessages = this.getFilteredMessages(category, queue);

            if (predefinedMessages.length === 0) {
                return null;
            }

            predefinedMessageList.push(
                <Box className="predefined-search-category" key={`category-${category}`}>
                    {category}
                </Box>
            );

            predefinedMessages.map((message, i) => {
                predefinedMessageList.push(this.renderPredefinedMessageItem(message, `${category}-${message.id || i}`));
                return null;
            });

            return null;
        });

        if (predefinedMessageList.length === 0)
            return (
                <Box p={2} pt={0}>
                    Nenhum mensagem predefinida encontrada.
                </Box>
            )

        return predefinedMessageList;
    }

    predefinedMessageList = (key, queue) => {
        if (this.state.searchAllCategories && this.getActiveSearch()) {
            return this.predefinedMessageSearchList(queue);
        }

        const predefinedMessages = this.getFilteredMessages(key, queue);
        const predefinedMessageList = []

        if ((predefinedMessages || '').length === 0)
            return (
                <Box p={2} pt={0}>
                    Nenhum mensagem predefinida encontrada.
                </Box>
            )

        predefinedMessages && predefinedMessages.length && predefinedMessages.map((message, i) => {
            predefinedMessageList.push(this.renderPredefinedMessageItem(message, message.id || i));
            return null;
        });

        return predefinedMessageList;
    }

    filterPredefMessages = () => {
        
        const queue_id = this.props.activeChat?.queue?.id;
        const allowShowAll = !getSession()?.profile?.enterprise?.hide_predefined_messages_show_all;
        const showAll = allowShowAll ? this.state.showAll : false;

        if (!allowShowAll && this.state.showAll) {
            localStorage.setItem('predefinedMessagesShowAll', 'false');
        }
        
        if (queue_id && !showAll) {
            // Filter predefined messages to only include those matching the current queue_id
            const filteredMessages = {};
            Object.keys(this.props.predefinedMessages).forEach(category => {
                const messagesInCategory = this.props.predefinedMessages[category];
                const filteredMessagesInCategory = messagesInCategory.filter((message) =>
                    this.messageMatchesQueueSelection(message, [queue_id])
                );
                
                // Only add the category if it has messages after filtering
                if (filteredMessagesInCategory.length > 0) {
                    filteredMessages[category] = filteredMessagesInCategory;
                } else {
                    filteredMessages[category] = [];
                }
            });

            const categoryWithMostMessages = Object.keys(filteredMessages).reduce((a, b) => 
                (filteredMessages[a]?.length || 0) > (filteredMessages[b]?.length || 0) ? a : b
            , "");

            this.setState({
                predefinedMessages: filteredMessages,
                category: categoryWithMostMessages,
                queue: queue_id,
                showAll: showAll
            });
        } else {
            this.setState({
                predefinedMessages: this.props.predefinedMessages,
                category: Object.keys(this.props.predefinedMessages)[0] || "",
                queue: 0,
                showAll: showAll
            });
        }
    }

    componentWillMount() {
        this.filterPredefMessages();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.predefinedMessages !== this.props.predefinedMessages) {
            this.filterPredefMessages();
        }
    }


    render() {
        return (
            <div>
                <ConfirmationDialogCaptcha
                    open={this.state.showDeleteCategoryDialog}
                    title="Excluir categoria"
                    description={
                        <Box>
                            <Box sx={{ color: 'error.main', mb: 2 }}>
                                Todas as mensagens da categoria <strong>{this.state.category}</strong> serão excluídas.
                            </Box>
                            <Box>
                                Para confirmar a ação, digite: <strong>remover</strong> no campo abaixo.
                            </Box>
                        </Box>
                    }
                    handleClose={() => this.setState({ showDeleteCategoryDialog: false })}
                    handleSubmit={this.deleteCategory}
                    captcha="remover"
                    alternateYes="Confirmar"
                    alternateNo="Cancelar"
                />
                <Dialog keepMounted
                    open={this.props.open}
                    onClose={this.props.handleClose}
                    aria-labelledby="customized-dialog-title"
                    fullWidth
                    maxWidth="sm"
                    disablePortal
                    disableEnforceFocus
                    sx={{position:'absolute'}}
                >
                    <DialogTitle id="customized-dialog-title" className={'group-flexbox'}>
                        <span>{this.props.title}</span>
                        <Tooltip title="Fechar" >
                            <Button className="action-button">
                                <CloseIcon onClick={this.props.handleClose} />
                            </Button>
                        </Tooltip>
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2}>
                            {!this.state.showAddCategory &&
                                <Box display="flex" flex={1}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="demo-simple-select-label">Categoria</InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            label="Categoria"
                                            defaultValue={this.state.category}
                                            onChange={this.onChangeCategory}
                                        >
                                            {Object.keys(this.state.predefinedMessages).filter(key => this.getFilteredMessages(key, this.state.queue)?.length > 0 || this.state.newEmptyCategories.has(key)).map((key, index) =>
                                                <MenuItem value={key}>{key}</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title="Nova categoria" placement="bottom" disableInteractive>
                                        <IconButton
                                            onClick={() => this.setState({ showAddCategory: true })}
                                            style={{ marginLeft: "10px" }}
                                            size="medium"
                                        >
                                            <AddIcon className="icon-add" />
                                        </IconButton>
                                    </Tooltip>
                                    {this.canDeleteCategory() && (
                                        <Tooltip title="Excluir categoria" placement="bottom" disableInteractive>
                                            <IconButton
                                                onClick={() => this.setState({ showDeleteCategoryDialog: true })}
                                                size="medium"
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            }
                            {this.state.showAddCategory &&
                                <Paper sx={{ p: "2px 4px", display: "flex", flex: 1, alignItems: "center", bgcolor: "#efefef" }}>
                                    <InputBase
                                        sx={{ ml: 1, flex: 1 }}
                                        placeholder="Nova categoria"
                                        onChange={this.onChangeNewCategory}
                                    />
                                    <Tooltip title="Adicionar" placement="bottom" disableInteractive>
                                        <IconButton onClick={() => this.addCategory()}>
                                            <AddIcon className="icon-add" />
                                        </IconButton>
                                    </Tooltip>
                                    <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
                                    <Tooltip title="Cancelar" placement="bottom" disableInteractive>
                                        <IconButton onClick={() => this.setState({ showAddCategory: false })}>
                                            <CloseIcon className="icon-cancel" />
                                        </IconButton>
                                    </Tooltip>
                                </Paper>
                            }
                            <FormControl fullWidth size="small">
                                <InputLabel>Fila</InputLabel>
                                <Select
                                    multiple
                                    label="Fila"
                                    value={Array.isArray(this.state.queue) ? this.state.queue : this.normalizeQueueSelection(this.state.queue)}
                                    onChange={this.handleQueueChange}
                                    disabled={!this.state.showAll}
                                    renderValue={(selectedValues) => {
                                        const normalizedValues = this.normalizeQueueSelection(selectedValues);
                                        if (normalizedValues.includes(0)) {
                                            return "Qualquer Fila";
                                        }

                                        return normalizedValues
                                            .map((queueId) => getSession().profile?.enterprise?.queues.find((q) => q.id === queueId)?.description)
                                            .filter(Boolean)
                                            .join(", ");
                                    }}
                                >
                                    <MenuItem
                                        key={0}
                                        value={0}
                                        sx={this.selectedItemSx}
                                    >
                                        Qualquer Fila
                                    </MenuItem>
                                    <Divider />
                                    {this.getAvailableQueues().map((q) =>
                                        <MenuItem
                                            key={q.id}
                                            value={q.id}
                                            sx={this.selectedItemSx}
                                        >
                                            {q.description}
                                        </MenuItem>
                                    )
                                    }
                                </Select>
                            </FormControl>
                            <Box display="flex" gap={1} alignItems="center">
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Buscar mensagem"
                                    placeholder="Digite para filtrar"
                                    value={this.state.search}
                                    onChange={(event) => this.setState({ search: event.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <FormControlLabel
                                    className="predefined-search-all-categories"
                                    label="Todas categorias"
                                    control={<Checkbox
                                        size="small"
                                        checked={this.state.searchAllCategories}
                                        onChange={(event) => this.setState({ searchAllCategories: event.target.checked })}
                                    />}
                                />
                            </Box>
                            {!getSession()?.profile?.enterprise?.hide_predefined_messages_show_all && (
                                <FormControlLabel
                                    label={<InputLabel>Mostrar tudo</InputLabel>}
                                    control={<Checkbox
                                        checked={this.state.showAll}
                                        onChange={(event) => this.setState({ showAll: event.target.checked }, () => {
                                            // Re-filter messages based on the new showAll state
                                            localStorage.setItem('predefinedMessagesShowAll', this.state.showAll);
                                            this.filterPredefMessages();
                                        })}
                                    />
                                }/>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogContent 
                        sx={{ 
                            padding: "0 1.5rem", 
                            minHeight: "300px",
                            maxHeight: "400px",
                            overflow: "auto",
                            display: "flex",
                            flexDirection: "column"
                        }} 
                        ref={instance => this.content = instance}
                    >
                        <List sx={{ flex: 1, overflow: "auto" }}>
                            {this.predefinedMessageList(this.state.category, this.state.queue)}
                            {/* {Object.keys(this.state.predefinedMessages).length === 0 &&
                                <Box>Você não possui mensagens predefinidas.</Box>
                            } */}
                        </List>
                        <Dialog keepMounted
                            open={this.state.openEditMessage || this.state.openViewMessage}
                            onClose={() => this.setState({
                                openViewMessage: false,
                                openEditMessage: false,
                                textEdit: '',
                                editMessage: {}
                            })}
                            aria-labelledby="customized-dialog-title"
                            disablePortal
                            disableEnforceFocus
                            sx={{position:'absolute'}}
                        >
                            <DialogTitle id="customized-dialog-title"
                                style={{
                                    backgroundColor: "rgba(33, 68, 108, 0.06)",
                                    minWidth: "500px"
                                }}
                            >
                                {this.state.openViewMessage ?
                                    "Visualizar mensagem predefinida" :
                                    "Edição de mensagem predefinida"
                                }
                            </DialogTitle>
                            <DialogActions
                                style={{
                                    padding: "2rem",
                                    gap: '1rem',
                                    overflowWrap:'break-word'
                                }}
                            >
                                {!this.state.openViewMessage ?
                                    <EmojiTextarea
                                        text={this.state.textEdit}
                                        setText={(event) => this.setState({ textEdit: event.target.value })}
                                        setEmoji={(text) => this.setState({ textEdit: text })}
                                        onKeyDown={this.onKeyDownEditMessage}
                                        maxLength={1000}
                                        label="Mensagem"
                                        childComponent="MessageBox"
                                        dropdown={(<PredefinedDropdown target={(e) => this.setState({ textEdit: this.state.textEdit+e })}/>)}
                                    />
                                    :
                                    <Box sx={{ width: "100%", maxHeight: 400, overflow: 'auto' }}>
                                        {this.state.editMessage?.is_html 
                                            ? ReactHtmlParser(this.state.textEdit)
                                            : emojiReplaceJSX(this.state.textEdit)
                                        }
                                    </Box>}
                                {this.state.openEditMessage &&
                                    <Button
                                        onClick={() => this.saveEditMessage()}
                                        color="primary">
                                        Salvar
                                    </Button>
                                }
                            </DialogActions>
                        </Dialog>
                    </DialogContent>
                    {this.state.category && !this.state.category?.includes(' - Geral')
                        ? <DialogActions style={{ padding: "10px 20px 15px 1.5rem", gap: "1rem" }}>
                            <EmojiTextarea
                                text={this.state.text}
                                setText={(event) => this.setState({ text: event.target.value })}
                                setEmoji={(text) => this.setState({ text: text })}
                                onKeyDown={this.onKeyDownPredefinedMessage}
                                maxLength={1000}
                                label="Mensagem"
                                childComponent="MessageBox"
                                dropdown={(<PredefinedDropdown target={(e) => this.setState({ text: this.state.text+e })}/>)}
                            />
                            <Button
                                onClick={() => this.addPredefinedMessage()}
                                color="primary"
                                disabled={this.state.text.trim() === ""}
                            >
                                Adicionar
                            </Button>
                        </DialogActions>
                        : <Box mt={2}></Box>
                    }
                </Dialog>
            </div>
        );
    }
}

export default PredefinedMessageDialog;
